import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Open } from "../../../compoment/icon";
import StationThemeLayout from "../../../layouts/StationThemeLayout";
import type { RootState } from "../../../redux/store";
import {
  getTransformCustomLineMaxLength,
  isAiSelfieCaricatureHobbyPostPaymentActive,
  shouldUseHobbyCaricatureFlowForStoredAiSelfieArt,
} from "../../../services/stylePromptResolver";
import { readActiveStoredStation } from "../../../utils/stationFlowStorage";

export const HOBBY_TRANSFORMATION_CUSTOM_LINE_KEY = "hobbyTransformationCustomLine";

const PROMPT_EXIT_MS = 500;

const HobbyWriteLine = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hobbyPersonImages, hobbyPersonCompletedResults } = useSelector(
    (s: RootState) => s.uploadFlow
  );

  const [customLine, setCustomLine] = useState("");
  const [promptLeaving, setPromptLeaving] = useState(false);
  const promptExitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const continuedRef = useRef(false);

  const stationData = useMemo(() => {
    const fromNav = (location.state as { stationData?: Record<string, unknown> } | null)
      ?.stationData;
    if (fromNav && typeof fromNav === "object") {
      return fromNav as Record<string, unknown> & { type?: string; name?: string };
    }
    try {
      const selectedRaw = localStorage.getItem("selectedStation");
      const fromSelected = selectedRaw ? JSON.parse(selectedRaw) : null;
      const fromStorage = JSON.parse(localStorage.getItem("stationData") || "{}");
      return fromSelected || fromStorage;
    } catch {
      return readActiveStoredStation() || {};
    }
  }, [location.state]);

  const routeSlug = useMemo(() => {
    if (
      isAiSelfieCaricatureHobbyPostPaymentActive() ||
      shouldUseHobbyCaricatureFlowForStoredAiSelfieArt()
    ) {
      return "hobby-caricature";
    }
    const t = stationData?.type;
    return typeof t === "string" && t.trim() ? t : "hobby-caricature";
  }, [stationData?.type]);

  // Hobby write-line is caricature flow → 30; other textarea arts use 20.
  const customLineMaxLength = useMemo(
    () => getTransformCustomLineMaxLength(routeSlug, stationData?.type, stationData?.name),
    [routeSlug, stationData?.name, stationData?.type]
  );

  const currentPersonIndex = useMemo(() => {
    const fromState = (location.state as { currentPersonIndex?: number } | null)
      ?.currentPersonIndex;
    if (typeof fromState === "number" && fromState >= 0) return fromState;
    return Math.max(0, hobbyPersonCompletedResults.length);
  }, [location.state, hobbyPersonCompletedResults.length]);

  const previewUrl = hobbyPersonImages[currentPersonIndex] || "";

  const navState = useMemo(
    () => (location.state as Record<string, unknown> | null) || {},
    [location.state]
  );

  useEffect(() => {
    return () => {
      if (promptExitTimerRef.current) clearTimeout(promptExitTimerRef.current);
    };
  }, []);

  // Missing selfie — send back to media library instead of a blank preview.
  useEffect(() => {
    if (previewUrl) return;
    navigate(`/${routeSlug}/select-media-library`, { replace: true });
  }, [previewUrl, navigate, routeSlug]);

  const goToCreating = useCallback(
    (line: string) => {
      if (continuedRef.current) return;
      continuedRef.current = true;

      const normalized = line.trim().slice(0, customLineMaxLength);
      try {
        if (normalized) {
          localStorage.setItem(HOBBY_TRANSFORMATION_CUSTOM_LINE_KEY, normalized);
        } else {
          localStorage.removeItem(HOBBY_TRANSFORMATION_CUSTOM_LINE_KEY);
        }
      } catch {
        /* ignore quota */
      }

      navigate(`/${routeSlug}/creating-person-hobby`, {
        replace: true,
        state: {
          ...navState,
          stationData,
          currentPersonIndex,
          customLine: normalized,
        },
      });
    },
    [navigate, routeSlug, navState, stationData, currentPersonIndex, customLineMaxLength]
  );

  const continueWithLine = (line?: string) => {
    if (promptLeaving || continuedRef.current) return;
    const normalized = (line ?? "").trim().slice(0, customLineMaxLength);
    setCustomLine(normalized);
    setPromptLeaving(true);
    promptExitTimerRef.current = setTimeout(() => {
      goToCreating(normalized);
    }, PROMPT_EXIT_MS);
  };

  return (
    <StationThemeLayout
      className={`caricatureTransformationSection is-prompt-step${promptLeaving ? " is-prompt-leaving" : ""}`}
      station={stationData as { backgroundImageUrl?: string; type?: string; name?: string }}
      showStationLogo
    >
      <div className="imgSketchContainer">
        <div className="imgSection imgSectionTransformation">
          <div
            className="transformationStage transformationPhase-1 is-prompt-idle"
            data-phase={1}
          >
            <div className="transformationImageWrap">
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt="Selected selfie preview"
                  className="caricatureImg transformationPreview"
                />
              ) : null}
            </div>
          </div>

          <div className={`transformationPromptStep${promptLeaving ? " is-leaving" : ""}`}>
            <div className="transformationPromptHeading">
              Add custom message to your photo<span>(Optional)</span>
            </div>
            <form
              className="transformationPromptForm"
              onSubmit={(event) => {
                event.preventDefault();
                continueWithLine(customLine);
              }}
            >
              <div className="transformationPromptField">
                <textarea
                  value={customLine}
                  placeholder="Add custom message to your photo"
                  aria-label="Add custom message to your photo"
                  maxLength={customLineMaxLength}
                  disabled={promptLeaving}
                  onChange={(event) =>
                    setCustomLine(event.target.value.slice(0, customLineMaxLength))
                  }
                />
                <div className="transformationPromptCharCount" aria-live="polite">
                  {customLine.length}/{customLineMaxLength}
                </div>
              </div>
              <button type="submit" disabled={promptLeaving}>
                Submit
              </button>
            </form>
            <button
              type="button"
              className="transformationPromptSkip"
              disabled={promptLeaving}
              onClick={() => continueWithLine()}
            >
              Skip <span aria-hidden="true"><Open /></span>
            </button>
          </div>
        </div>
      </div>
    </StationThemeLayout>
  );
};

export default HobbyWriteLine;
